import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { Printer, FileDown } from 'lucide-react';
import { exportCSV } from './reportUtils';
import { useCompanyInfo, generatePrintHTML, openPrint } from './ReportExporter';

// IRP apportions each truck's registration fee by that truck's own
// state-mileage percentage for the full registration year (unlike IFTA,
// which is quarterly and company-wide, not per-vehicle). Reuses the same
// state_mileage source logged for IFTA -- just re-sliced per unit, per
// full year, with each state's share of that unit's total miles.
export default function IRPStateMilesReport() {
  const [logs, setLogs] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const company = useCompanyInfo();

  useEffect(() => {
    Promise.all([
      client.entities.IFTAMileageLog.list('-trip_date', 2000),
      client.entities.Asset.filter({ asset_type: 'Truck' }, 'unit_number', 200),
      client.entities.Load.list('-delivery_date', 2000),
    ]).then(([l, a, ld]) => { setLogs(l); setAssets(a); setLoads(ld); setLoading(false); });
  }, []);

  const unitByDriverEmail = new Map(
    assets.filter(a => a.assigned_driver_email).map(a => [a.assigned_driver_email.toLowerCase(), a.unit_number])
  );
  const loadById = new Map(loads.map(l => [l.id, l]));

  const filtered = logs.filter(l => l.year === year);

  const unitStateMap = {};
  filtered.forEach(l => {
    // Each mileage log is tied to a specific load, and NewLoadModal/
    // EditLoadModal/LoadBoard now snapshot which unit actually hauled
    // that load onto it (truck_unit) -- prefer that ground truth over
    // "whichever truck this driver is CURRENTLY assigned to," which
    // silently misattributes a log once the driver's truck changes.
    const unit = loadById.get(l.load_id)?.truck_unit
      || unitByDriverEmail.get((l.driver_email || '').toLowerCase())
      || 'Unassigned';
    try {
      const miles = JSON.parse(l.state_mileage || '[]');
      miles.forEach(({ state, miles: m }) => {
        if (!unitStateMap[unit]) unitStateMap[unit] = {};
        unitStateMap[unit][state] = (unitStateMap[unit][state] || 0) + (parseFloat(m) || 0);
      });
    } catch { /* not the expected shape -- skip */ }
  });

  const rows = [];
  Object.entries(unitStateMap).forEach(([unit, states]) => {
    const unitTotal = Object.values(states).reduce((s, m) => s + m, 0);
    Object.entries(states).sort((a, b) => b[1] - a[1]).forEach(([state, miles]) => {
      rows.push({
        'Unit #': unit,
        State: state,
        Miles: Math.round(miles),
        '% of Unit': unitTotal > 0 ? ((miles / unitTotal) * 100).toFixed(1) : '0.0',
        unitTotal: Math.round(unitTotal),
      });
    });
  });
  rows.sort((a, b) => a['Unit #'].localeCompare(b['Unit #']) || b.Miles - a.Miles);

  const grandTotal = Object.values(unitStateMap).reduce((s, states) => s + Object.values(states).reduce((s2, m) => s2 + m, 0), 0);
  const unitCount = Object.keys(unitStateMap).length;

  const csvRows = rows.map(({ unitTotal: _unitTotal, ...r }) => r);

  const handlePrint = () => {
    const cols = ['Unit #', 'State', 'Miles', '% of Unit'];
    const dataRows = rows.map(r => [
      { value: r['Unit #'], class: 'bold' }, r.State, r.Miles.toLocaleString(), `${r['% of Unit']}%`,
    ]);
    const totalsRow = ['TOTAL', '', { value: Math.round(grandTotal).toLocaleString(), class: 'gold' }, ''];
    const cards = [
      { label: 'Units', value: unitCount, color: 'gold' },
      { label: 'Registration Year', value: year, color: '' },
      { label: 'Total Miles', value: Math.round(grandTotal).toLocaleString(), color: 'positive' },
    ];
    const html = generatePrintHTML('IRP State Miles Report', `${year}`, cols, dataRows, totalsRow, company, cards);
    openPrint(html);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Registration Year</label>
          <select value={year} onChange={e => setYear(Number(e.target.value))}
            className="px-3 py-1.5 rounded-lg bg-transparent border border-input text-foreground text-xs">
            {[2023, 2024, 2025, 2026].map(y => <option key={y} value={y} className="bg-background">{y}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => exportCSV(csvRows, `irp-state-miles-${year}.csv`)} disabled={rows.length === 0}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/20 border border-primary/40 text-primary text-xs font-semibold hover:bg-primary/30 transition-colors disabled:opacity-40">
            Export CSV
          </button>
          <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><Printer className="w-4 h-4" /> Print</button>
          <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><FileDown className="w-4 h-4" /> Export PDF</button>
        </div>
      </div>
      <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 px-4 py-3">
        <p className="text-xs text-amber-400 font-semibold">⚠ Review with your IRP administrator before filing. Loads with no unit-assigned driver on file appear under "Unassigned".</p>
      </div>
      <div className="glass-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              {['Unit #', 'State', 'Miles', '% of Unit'].map(h => (
                <th key={h} className="text-left px-3 py-2.5 text-muted-foreground font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r, i) => (
              <tr key={i} className="hover:bg-white/3">
                <td className="px-3 py-2.5 font-semibold text-primary">{r['Unit #']}</td>
                <td className="px-3 py-2.5 text-foreground">{r.State}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{r.Miles.toLocaleString()}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{r['% of Unit']}%</td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t border-border bg-white/5 font-bold">
                <td colSpan={2} className="px-3 py-2.5 text-foreground">TOTAL ({unitCount} units)</td>
                <td className="px-3 py-2.5 text-foreground">{Math.round(grandTotal).toLocaleString()}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
        {rows.length === 0 && !loading && <p className="text-center text-muted-foreground py-8 text-sm">No IFTA mileage data for {year}.</p>}
        {loading && <p className="text-center text-muted-foreground py-8 text-sm">Loading...</p>}
      </div>
    </div>
  );
}
